import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';
import { mediaQueryLarge } from '@theme/utilities';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });
    mediaQueryLarge.addEventListener('change', this.#pauseHiddenLayoutMedia, { signal });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  /**
   * Pauses deferred media inside inactive slideshow slides.
   * @param {SlideshowSelectEvent} event
   */
  #handleSlideshowSelect = (event) => {
    const slideshow = this.refs.slideshow;
    if (!slideshow?.contains(event.target)) return;

    const activeSlide = event.detail.slide;

    for (const slide of slideshow.slides ?? []) {
      if (slide === activeSlide) continue;
      this.#pauseDeferredMediaIn(slide);
    }
  };

  /**
   * Grid galleries render separate slideshow (mobile) and grid (desktop) trees.
   * Pause videos in the layout that is currently hidden after a breakpoint change.
   */
  #pauseHiddenLayoutMedia = () => {
    if (this.presentation !== 'grid') return;

    const hiddenContainer = mediaQueryLarge.matches
      ? this.querySelector('slideshow-component')
      : this.querySelector('.media-gallery__grid');

    if (hiddenContainer) {
      this.#pauseDeferredMediaIn(hiddenContainer);
    }
  };

  /**
   * @param {ParentNode} container
   */
  #pauseDeferredMediaIn(container) {
    for (const media of container.querySelectorAll('deferred-media')) {
      if ('pauseMedia' in media && typeof media.pauseMedia === 'function') {
        media.pauseMedia();
      }
    }
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
